<template>
    <div v-if="!hasDirectDebitConsent">
        <v-form v-model="valid">
            <v-alert v-if="isBlocked" dense text type="error" border="left" icon="mdi-alert-circle">
                {{$translate('You are blocked and can not sign direct debit')}}
            </v-alert>

            <v-alert v-if="errorMessage" type="error">
                {{$translate(errorMessage)}}
            </v-alert>

            <v-alert v-if="personalCodeNumber && isValidPersonalCode(personalCodeNumber) && !ageOkForInvoice" color="error" class="white--text">
                <v-icon color="white">mdi-information-outline</v-icon>
                {{$translate('You must be over 18 years old to make purchases by direct debit.')}}
            </v-alert>

            <!-- Legal Entity Indicator -->
            <div class="py-5 px-2">
                <strong>{{$translate('Legal entity')}}</strong>&nbsp;&nbsp;&nbsp;
                {{getSelectedSiteLegalEntity() && getSelectedSiteLegalEntity().name}}, {{getSelectedSiteLegalEntity() && getSelectedSiteLegalEntity().registration_number}}
            </div>

            <v-text-field :readonly="!!$store.state.user.personalCodeNumber" :label="$translate('Personal code number')" v-model="personalCodeNumber"  type="text" inputmode="numeric" :rules="[pCodeOk]" />

            <v-text-field :label="$translate('Clearing number')" v-model="clearingNumber" type="number" inputmode="numeric" min="1000" max="99999" :rules="[clearingNumberOk]">
                <template v-slot:append-outer>
                    <v-img height=50 width=100 position="top" :src="$api.getAssetUrl('/assets/img/banks/' + bank.image)" v-if="bank && bank.image" contain/>
                    <span v-else-if="bank">{{bank.name}}</span>
                    <div v-else style="width: 20px"></div>
                </template>
            </v-text-field>

            <v-text-field :label="$translate('Bank account number')" v-model="bankAccountNumber" type="number" inputmode="numeric" min="0" :rules="[accountNumberOk]" />

            <div v-if="processing">
                <qrcode-vue v-if="qrcode" :value="qrcode" :size="size" level="H" class="ml-3"></qrcode-vue>
                <div class="my-2">{{$translate('Open the BankID app to continue')}}</div>
                <zoezi-button v-if='mobile' @click="openBankIDApp()" class="my-2">{{ $translate('Open BankID on this device') }}</zoezi-button>
            </div>

            <div class="d-flex">
                <v-btn class="zoezi-button-thin" outlined @click="cancel" v-if="showCancel || processing" color="primary">{{$translate('Cancel')}}</v-btn>
                <v-spacer />
                <v-btn class="zoezi-button-thin" :disabled="!valid || !ageOkForInvoice || isBlocked" @click="sign" v-if="!processing" color="primary">{{$translate('Sign consent')}}</v-btn>
            </div>
        </v-form>
    </div>
</template>

<script>
import QrcodeVue from 'qrcode.vue';
import Banks from '../services/Banks.js'
import { isValidPersonalCode, getAgeFromPersonalCodeNumber, isExpoApp, isMobile, getMobileDeviceType } from '../util.js';
export default {
    name: 'zoezi-sign-directdebit-consent',

    zoezi: {
        title: 'direct debit consent'
    },

    props: {
        showCancel: {
            type: Boolean,
            title: 'Show cancel button',
            default: false
        },
        legalEntityId: {
            type: Number,
            default: null
        }
    },

    components: {
        QrcodeVue
    },

    data: () => ({
        valid: false,
        bankAccountNumber: null,
        clearingNumber: null,
        banks: Banks.banks,
        personalCodeNumber: null,
        processing: false,
        errorMessage: null,
        signId: null,
        unsubscribeChannel: null,
        autostarttoken: null,
        qrcode: null
    }),

    watch: {
        '$store.state.user.personalCodeNumber': {
            immediate: true,
            handler(pcode) {
                if (pcode) {
                    this.personalCodeNumber = pcode;
                }
            }
        }
    },

    computed: {
        bank() {
            return Banks.getBankFromClearingNumber(this.clearingNumber);
        },
        mobile() {
            return isMobile();
        },
        mobileType() {
            return getMobileDeviceType();
        },
        ageOkForInvoice() {
            let pcode = this.personalCodeNumber;
            let age = getAgeFromPersonalCodeNumber(pcode);
            return age >= 18;
        },
        isBlocked() {
            return this.$store.state.user?.isBlocked('block_autogiro');
        },
        hasDirectDebitConsent() {
            if (this.legalEntityId && this.$store.state.user?.consents) {
                return !!this.$store.state.user.consents[this.legalEntityId];
            }
            return this.$store.state.user?.hasConsent();
        }
    },

    methods: {
        clearingNumberOk(clearingNumber) {
            let bank = Banks.getBankFromClearingNumber(clearingNumber);
            if (!bank) {
                return this.$translate('No valid clearing number');
            }

            return true;
        },
        accountNumberOk() {
            let ok = Banks.validateAccountNumber(this.clearingNumber, this.bankAccountNumber);
            if (!ok) {
                return this.$translate('No valid account number');
            }

            return true;
        },
        openBankIDApp() {
            if (isExpoApp() && window.ReactNativeWebView) {
                window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'bankid', data: this.autostarttoken }));
            } else if (this.mobileType == 'ios') {
                let redirect_uri ='';
                window.location.href = 'https://app.bankid.com/?autostarttoken={0}&redirect={1}'.format(this.autostarttoken, encodeURIComponent(redirect_uri));
            } else if (this.mobileType == 'android') {
                window.open('bankid:///?autostarttoken={0}&redirect='.format(this.autostarttoken));
            } else {
                this.$store.commit('showSnackBar', {text: this.$translate('Device not supported')});
            }
        },
        sign() {
            let data = {
                personalCodeNumber: this.$store.state.user.personalCodeNumber || this.personalCodeNumber,
                account: this.bankAccountNumber,
                clearingNumber: this.clearingNumber,
                legal_entity_id: this.legalEntityId || this.getSelectedSiteLegalEntity()?.id
            }

            this.processing = true;
            this.autostarttoken = null;
            this.$api.post('/api/memberapi/contract/sign/autogiro', data).then((response) => {
                if (!response.ok) {
                    response.json().then(error => {
                        this.errorMessage = error.message || 'Error during signing';
                        this.processing = false;
                    }).catch(() => {
                        this.errorMessage = 'Error during signing';
                        this.processing = false;
                    });
                    return;
                }

                response.json().then(data => {
                    if (data.aborted) {
                        this.errorMessage = 'BANKID_ERROR_DURING_SIGNING';
                        this.processing = false;
                        return;
                    }

                    this.signId = data.signId;
                    this.autostarttoken = data.autostarttoken;

                    // Only open on Android or in app. Others will have to click the button
                    if (this.mobileType == 'android' || isExpoApp()) {
                        this.openBankIDApp(data.autostarttoken);
                    }
                    this.unsubscribeChannel = this.$api.subscribeChannel(data.signId, (resp) => {
                        if (resp.status == 'consent') {
                            console.log('Consent done')
                            this.refetchUser().then(() => {
                                this.processing = false;
                                this.$store.commit('showSnackBar', {text: 'Autogiromedgivande signerat', color: 'success'});
                            });
                            if (this.unsubscribeChannel) {
                                this.unsubscribeChannel();
                            }
                        } else if (resp.status == 'qrcode') {
                            this.qrcode = resp.qrcode;
                        } else if (resp.aborted) {
                            console.log('Consent sign aborted: ' + resp.status)
                            const errorByStatus = {
                                'USER_CANCEL': 'USER_ABORTED_BANKID_SIGNING',
                                'WRONG_PERSONAL_CODE': 'BANKID_WRONG_PERSONAL_CODE',
                                'PERSONAL_CODE_IN_USE': 'BANKID_PERSONAL_CODE_IN_USE',
                            };
                            this.errorMessage = errorByStatus[resp.status] || 'BANKID_ERROR_DURING_SIGNING';
                            this.processing = false;
                            if (this.unsubscribeChannel) {
                                this.unsubscribeChannel();
                            }
                            this.qrcode = null;
                        }
                        else {
                            console.log('Autogiro signing status:' + JSON.stringify(response));
                        }
                    })
                })
            })
        },
        pCodeOk(x) {
            if (x && !isValidPersonalCode(x)) {
                return 'Felaktigt personnummer';
            }

            return true;
        },
        isValidPersonalCode(x) {
            return isValidPersonalCode(x);
        },
        cancel() {
            if (this.signId) {
                this.$api.post('/api/contract/sign/cancel', null, {id: this.signId}).then(() => this.processing = false);
                this.signId = null;
            }

            if (this.unsubscribeChannel) {
                this.unsubscribeChannel();
            }

            this.errorMessage = null;

            this.$emit('cancel')
        },
        refetchUser() {
            return this.$api.get('/api/memberapi/get/current').then(user => {
                console.log('Got new user info');
                this.$store.commit('setUserData', {user})
            });
        },
        getSelectedSiteLegalEntity() {
            if (this.legalEntityId) {
                const site = this.$store.state.settings?.sites?.find(
                    s => s.legal_entity?.id === this.legalEntityId
                );
                if (site) return site.legal_entity;
            }
            const selectedSite = this.$store.state.settings?.sites?.find(
                site => site.id === this.$store.state.selectedSiteId
            );
            return selectedSite?.legal_entity;
        }
    }
}
</script>
